import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { PlayerSave, ProductionRates, Resources } from '@annopolis/shared';
import {
  computeProductionRates,
  nextUnitCost,
  bulkUnitCost,
  maxAffordable,
  getAutobuyMaxUnit,
  hasSmartAllocation,
  techCost,
  getTechDef,
  getUnitDef,
  getUnitsForTier,
  formatNumber,
  computeAttackPower,
  computeDefensePower,
} from '@annopolis/shared';

// ─── Store Shape ────────────────────────────────────────────────────────────────

interface GameState {
  // Core save
  save: PlayerSave;
  // Derived — recomputed on each tick
  rates: ProductionRates;
  attackPower: number;
  defensePower: number;
  // UI
  activeTab: 'units' | 'tech' | 'pvp' | 'shard' | 'automation';
  selectedBuyQty: 1 | 10 | 100 | 'max';
  notificationQueue: Notification[];
  // Runtime
  isLoaded: boolean;
  lastTickMs: number;
  // Boost (from server)
  activeBoostMult: number;
  offlineCapSeconds: number;
  // Autobuy
  autobuyEnabled: boolean;

  // Actions
  tick: (nowMs: number) => void;
  buyUnit: (unitId: number, qty?: number) => boolean;
  researchTech: (techId: string) => boolean;
  setActiveTab: (tab: GameState['activeTab']) => void;
  setSelectedBuyQty: (qty: GameState['selectedBuyQty']) => void;
  loadSave: (save: PlayerSave, boostMult?: number, offlineCapSec?: number) => void;
  applyOfflineGains: (gains: Resources) => void;
  pushNotification: (n: Notification) => void;
  dismissNotification: (id: string) => void;
  setAutobuy: (enabled: boolean) => void;
  applyRaidDebuff: (debuff: number, durationSec: number) => void;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'pvp';
  message: string;
  at: number;
}

// ─── Default Save ───────────────────────────────────────────────────────────────

function defaultSave(): PlayerSave {
  return {
    playerId: '',
    tier: 1,
    ascensions: 0,
    resources: { primary: 0, knowledge: 0, energy: 0, pvp: 0 },
    lifetimeResources: { primary: 0, knowledge: 0, energy: 0, pvp: 0 },
    units: {},
    tech: {},
    prestigeMult: 1,
    ascensionMult: 1,
    lastSaved: Date.now(),
    offlineStart: Date.now(),
    activeRaids: [],
    raidCooldowns: {},
  };
}

// ─── Active raid debuff state (module-level, not reactive) ─────────────────────
let raidDebuffUntil = 0;
let raidDebuffAmount = 0;

// ─── Store ──────────────────────────────────────────────────────────────────────

export const useGameStore = create<GameState>()(
  subscribeWithSelector((set, get) => ({
    save: defaultSave(),
    rates: { primaryPerSec: 0, knowledgePerSec: 0, energyPerSec: 0, pvpPerSec: 0 },
    attackPower: 0,
    defensePower: 0,
    activeTab: 'units',
    selectedBuyQty: 10,
    notificationQueue: [],
    isLoaded: false,
    lastTickMs: Date.now(),
    activeBoostMult: 1,
    offlineCapSeconds: 8 * 3600,
    autobuyEnabled: false,

    // ── Tick ──────────────────────────────────────────────────────────────────
    tick: (nowMs: number) => {
      const { save, rates, activeBoostMult, autobuyEnabled } = get();
      const dt = Math.min((nowMs - get().lastTickMs) / 1000, 5); // cap at 5s to handle tab background

      // Apply raid debuff
      const debuffMult = nowMs < raidDebuffUntil ? (1 - raidDebuffAmount) : 1;
      const effectiveBoost = activeBoostMult * debuffMult;

      const newResources: Resources = {
        primary: save.resources.primary + rates.primaryPerSec * dt * effectiveBoost,
        knowledge: save.resources.knowledge + rates.knowledgePerSec * dt * effectiveBoost,
        energy: save.resources.energy + rates.energyPerSec * dt * effectiveBoost,
        pvp: save.resources.pvp + rates.pvpPerSec * dt,
      };
      const newLifetime: Resources = {
        primary: save.lifetimeResources.primary + rates.primaryPerSec * dt * effectiveBoost,
        knowledge: save.lifetimeResources.knowledge + rates.knowledgePerSec * dt * effectiveBoost,
        energy: save.lifetimeResources.energy + rates.energyPerSec * dt * effectiveBoost,
        pvp: save.lifetimeResources.pvp + rates.pvpPerSec * dt,
      };

      const newSave = { ...save, resources: newResources, lifetimeResources: newLifetime };

      // Autobuy
      if (autobuyEnabled) {
        autobuyTick(newSave, get);
      }

      // Recompute rates (in case autobuy changed units)
      const newRates = computeProductionRates(
        newSave.tier, newSave.units, newSave.tech, newSave.lifetimeResources,
        newSave.prestigeMult, newSave.ascensionMult, effectiveBoost
      );

      set({
        save: newSave,
        rates: newRates,
        lastTickMs: nowMs,
        attackPower: computeAttackPower(newSave.tier, newSave.units, newSave.tech),
        defensePower: computeDefensePower(newSave.tier, newSave.units, newSave.tech),
      });
    },

    // ── Buy Unit ──────────────────────────────────────────────────────────────
    buyUnit: (unitId: number, qty?: number) => {
      const { save, selectedBuyQty } = get();
      const def = getUnitDef(save.tier, unitId);
      if (!def) return false;

      const owned = save.units[unitId] ?? 0;
      const quantity = qty ?? (selectedBuyQty === 'max'
        ? maxAffordable(def, owned, save.resources.primary, save.tech)
        : selectedBuyQty);

      if (quantity <= 0) return false;

      const cost = quantity === 1
        ? nextUnitCost(def, owned, save.tech)
        : bulkUnitCost(def, owned, quantity, save.tech);

      if (save.resources.primary < cost) return false;

      const newUnits = { ...save.units, [unitId]: owned + quantity };
      const newResources = { ...save.resources, primary: save.resources.primary - cost };

      const newSave = { ...save, units: newUnits, resources: newResources };
      const newRates = computeProductionRates(
        newSave.tier, newSave.units, newSave.tech, newSave.lifetimeResources,
        newSave.prestigeMult, newSave.ascensionMult, get().activeBoostMult
      );

      set({
        save: newSave,
        rates: newRates,
        attackPower: computeAttackPower(newSave.tier, newSave.units, newSave.tech),
        defensePower: computeDefensePower(newSave.tier, newSave.units, newSave.tech),
      });
      return true;
    },

    // ── Research Tech ─────────────────────────────────────────────────────────
    researchTech: (techId: string) => {
      const { save } = get();
      const def = getTechDef(techId);
      if (!def) return false;

      const currentLevel = save.tech[techId] ?? 0;
      if (def.maxLevel !== -1 && currentLevel >= def.maxLevel) return false;

      const cost = techCost(def, currentLevel);
      if (save.resources.knowledge < cost) return false;

      // Check unlock requirements
      if (def.unlockRequires) {
        const req = def.unlockRequires;
        if (req.type === 'unit_owned' && req.unitId) {
          if ((save.units[req.unitId] ?? 0) < (req.amount ?? 1)) return false;
        }
        if (req.type === 'tech_level' && req.techId) {
          if ((save.tech[req.techId] ?? 0) < (req.techLevel ?? 1)) return false;
        }
      }

      const newTech = { ...save.tech, [techId]: currentLevel + 1 };
      const newResources = { ...save.resources, knowledge: save.resources.knowledge - cost };
      const newSave = { ...save, tech: newTech, resources: newResources };
      const newRates = computeProductionRates(
        newSave.tier, newSave.units, newSave.tech, newSave.lifetimeResources,
        newSave.prestigeMult, newSave.ascensionMult, get().activeBoostMult
      );

      set({ save: newSave, rates: newRates });
      return true;
    },

    // ── Load Save ─────────────────────────────────────────────────────────────
    loadSave: (save: PlayerSave, boostMult = 1, offlineCapSec = 8 * 3600) => {
      const rates = computeProductionRates(
        save.tier, save.units, save.tech, save.lifetimeResources,
        save.prestigeMult, save.ascensionMult, boostMult
      );
      set({
        save,
        rates,
        attackPower: computeAttackPower(save.tier, save.units, save.tech),
        defensePower: computeDefensePower(save.tier, save.units, save.tech),
        isLoaded: true,
        lastTickMs: Date.now(),
        activeBoostMult: boostMult,
        offlineCapSeconds: offlineCapSec,
      });
    },

    applyOfflineGains: (gains: Resources) => {
      const { save } = get();
      const newResources: Resources = {
        primary: save.resources.primary + gains.primary,
        knowledge: save.resources.knowledge + gains.knowledge,
        energy: save.resources.energy + gains.energy,
        pvp: save.resources.pvp + gains.pvp,
      };
      const newLifetime: Resources = {
        primary: save.lifetimeResources.primary + gains.primary,
        knowledge: save.lifetimeResources.knowledge + gains.knowledge,
        energy: save.lifetimeResources.energy + gains.energy,
        pvp: save.lifetimeResources.pvp + gains.pvp,
      };
      set({ save: { ...save, resources: newResources, lifetimeResources: newLifetime } });
      if (gains.primary > 0) {
        get().pushNotification({
          id: `offline_${Date.now()}`,
          type: 'info',
          message: `Welcome back! Collected ${formatNumber(gains.primary)} Matter while away.`,
          at: Date.now(),
        });
      }
    },

    setActiveTab: (tab) => set({ activeTab: tab }),
    setSelectedBuyQty: (qty) => set({ selectedBuyQty: qty }),
    setAutobuy: (enabled) => set({ autobuyEnabled: enabled }),

    pushNotification: (n) =>
      set((s) => ({ notificationQueue: [...s.notificationQueue.slice(-9), n] })),

    dismissNotification: (id) =>
      set((s) => ({ notificationQueue: s.notificationQueue.filter((n) => n.id !== id) })),

    applyRaidDebuff: (debuff: number, durationSec: number) => {
      raidDebuffUntil = Date.now() + durationSec * 1000;
      raidDebuffAmount = debuff;
      get().pushNotification({
        id: `debuff_${Date.now()}`,
        type: 'pvp',
        message: `You were raided! -${(debuff * 100).toFixed(0)}% production for ${Math.ceil(durationSec / 60)}m`,
        at: Date.now(),
      });
    },
  }))
);

// ─── Autobuy logic ──────────────────────────────────────────────────────────────

function autobuyTick(save: PlayerSave, get: () => GameState) {
  const maxUnit = getAutobuyMaxUnit(save.tech);
  if (maxUnit === 0) return;

  const smart = hasSmartAllocation(save.tech);
  const units = getUnitsForTier(save.tier).filter((u) => u.id <= maxUnit);

  if (smart) {
    // Buy the unit with best ROI (lowest time-to-pay-back relative to current production)
    let bestUnit = -1;
    let bestROI = Infinity;
    for (const def of units) {
      const owned = save.units[def.id] ?? 0;
      const cost = nextUnitCost(def, owned, save.tech);
      if (cost > save.resources.primary) continue;
      // ROI proxy: cost / (marginal production gain) — simpler heuristic: prefer cheapest
      const roi = cost / Math.max(1, owned + 1);
      if (roi < bestROI) {
        bestROI = roi;
        bestUnit = def.id;
      }
    }
    if (bestUnit !== -1) get().buyUnit(bestUnit, 1);
  } else {
    // Simple: buy cheapest available unit
    for (const def of units) {
      const owned = save.units[def.id] ?? 0;
      const cost = nextUnitCost(def, owned, save.tech);
      if (cost <= save.resources.primary) {
        get().buyUnit(def.id, 1);
        break;
      }
    }
  }
}
